import { FC, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { favoriteStateStore } from '@modules/favorite/adapter/outbound/stores/favourite.store';
import { headerStore } from '@modules/header/adapter/outbound/stores/header.store';
import { productDetailStore } from '@modules/products/adapter/outbound/stores/productDetail.store';
import { productRelatedProductStore } from '@modules/products/adapter/outbound/stores/productRelatedProduct.store';
import { recommendTipsTricksStore } from '@modules/tipsAndTricks/adapter/outbound/stores/recommendTipsTricks.store';
import { userStore } from '@modules/user/adapter/outbound/stores/user.store';
import { BreadCrumbComponent } from '@components/breadCrumb';
import ProductDetail from '@components/product/detail/Detail';
import { ProductPackshot } from '@components/product/packshot';
import MobilePackshot from '@components/product/packshot/mobile/MobilePackshot';
import RelatedTips from '@components/product/relatedTips/RelatedTips';
import ProductDetailSkeleton from '@components/skeleton/product/detail/ProductDetail';
import theme from '@shared/assets/theme/globalTheme';
import { useScreen } from '@shared/helpers/screen.helper';
import { ModuleType } from '@shared/types/module.type';
import useLockedBody from '@shared/utils/hooks/useLockedBody.utils';
const RelatedProduct = dynamic(() => import('@components/product/relatedProduct/RelatedProduct'));
const RelatedRecipe = dynamic(() => import('@components/product/relatedRecipe/RelatedRecipe'));
const LoginModal = dynamic(() => import('@components/modal/login/LoginModal'));
const ProductDetailProvider: FC = () => {
  const router = useRouter();
  const {
    isMobile
  } = useScreen();
  const [_locked, setLocked] = useLockedBody(false, 'root');
  const [isLike, setIsLike] = useState<boolean>(false);
  const [isModalLoginOpen, setIsModalLoginOpen] = useState<boolean>(false);
  const {
    productDetail,
    fetchProductDetail
  } = productDetailStore(state => state);
  const {
    relatedProductList,
    fetchRelatedProduct
  } = productRelatedProductStore(state => state);
  const {
    recommendTipsTricks,
    fetchRecommendTipsTricks
  } = recommendTipsTricksStore(state => state);
  const {
    isHeaderHidden,
    setIsHeaderHidden
  } = headerStore(state => state);
  const {
    isFavoriteItem,
    updateFavoriteItemById,
    resetData
  } = favoriteStateStore(state => state);
  const {
    userDetail
  } = userStore(state => state);
  useEffect(() => {
    resetData();
  }, []);
  useEffect(() => {
    if (router.query.slug) {
      const slug = router.query.slug as string;
      fetchProductDetail(slug);
      if (recommendTipsTricks.length === 0) {
        fetchRecommendTipsTricks();
      }
    }
  }, [router.query.slug, userDetail]);
  useEffect(() => {
    if (productDetail.id) {
      fetchRelatedProduct(productDetail.id);
      setLocked(false);
    } else {
      setLocked(true);
    }
  }, [productDetail]);
  useEffect(() => {
    setIsLike(isFavoriteItem);
  }, [isFavoriteItem]);
  const onUpdateLikeState = () => {
    if (userDetail) {
      updateFavoriteItemById(productDetail.id, ModuleType.Product);
      setIsLike(prev => !prev);
    } else {
      setIsModalLoginOpen(true);
    }
  };
  return productDetail.id ? <Container>
            <Wrapper>
                <BreadCrumbComponent mainSlugColor={theme.colors.grey[40]} subSlugColor={theme.colors.grey[30]} arrowColor={theme.colors.grey[10]} pageName={productDetail.name} />
                <WrapperProduct>
                    {isMobile ? <MobilePackshot data={productDetail.packshotUrl} /> : <ProductPackshot data={productDetail.packshotUrl} />}

                    <WrapperMobile>
                        <ProductDetail data={productDetail} headerVisibility={isHeaderHidden} setHeaderVisibility={setIsHeaderHidden} isLike={isLike} onUpdateLikeState={onUpdateLikeState} />
                    </WrapperMobile>
                </WrapperProduct>
            </Wrapper>
            {relatedProductList.length > 0 && <RelatedProduct data={relatedProductList} productCategories={productDetail.productCategories} />}
            {productDetail.relatedRecipe.length > 0 && <RelatedRecipe data={productDetail.relatedRecipe} meatCategories={productDetail.meatCategories} />}
            <RelatedTips data={recommendTipsTricks} />
            <LoginModal isOpen={isModalLoginOpen} onClose={() => setIsModalLoginOpen(false)} />
        </Container> : <ProductDetailSkeleton />;
};
export default ProductDetailProvider;
const Container = styled.div.withConfig({
  displayName: "ProductDetailprovider__Container",
  componentId: "sc-1yjmvp3-0"
})(["", " background-color:#F9F9F9;"], {
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "center"
});
const Wrapper = styled.div.withConfig({
  displayName: "ProductDetailprovider__Wrapper",
  componentId: "sc-1yjmvp3-1"
})(["", " max-width:", ";@media (max-width:", "){", "}@media (max-width:", "){", "}"], {
  "width": "100%"
}, ({
  theme
}) => `${theme.layout.width}`, ({
  theme
}) => theme.breakpoint.lg, {
  "paddingLeft": "1rem",
  "paddingRight": "1rem"
}, ({
  theme
}) => theme.breakpoint.md, {
  "paddingLeft": "0px",
  "paddingRight": "0px"
});
const WrapperMobile = styled.div.withConfig({
  displayName: "ProductDetailprovider__WrapperMobile",
  componentId: "sc-1yjmvp3-2"
})(["@media (max-width:", "){", "}"], ({
  theme
}) => theme.breakpoint.md, {
  "marginLeft": "1rem",
  "marginRight": "1rem"
});
const WrapperProduct = styled.div.withConfig({
  displayName: "ProductDetailprovider__WrapperProduct",
  componentId: "sc-1yjmvp3-3"
})(["", " @media (max-width:", "){", "}"], {
  "marginTop": "0.5rem",
  "display": "grid",
  "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",
  "gap": "2rem"
}, ({
  theme
}) => theme.breakpoint.md, {
  "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
  "gap": "1rem"
});